import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { environment } from '../../../../../../environments/environment';
import type {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
} from '@fitogest/reactive-form-builder/interfaces';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import { OptionsComponent } from '../../../../../common/components/upload-file/upload-file.component';
import { AgroambientalModel } from '../../../../../models/tareas/agroambientales.model';
import { AgroambientalesFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-agroambientales-form',
    standalone: false,
    templateUrl: './agroambientales-form.component.html',
    styleUrls: ['./agroambientales-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgroambientalesFormComponent extends BaseForm<AgroambientalModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario agroambiental...';

    public form!: AgroambientalesFormDefinition;
    public override model: AgroambientalModel = {};
    public initialPreviousData: AgroambientalModel | null = null;
    public reactiveFormRecordId = '';
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.tareasApi.agroambientales.GET;
    public showDialogUploadFile = false;

    public optionsUploadFile: OptionsComponent = {
        name: '',
        directory: 'ecorregimenes',
        multiple: true,
        accept: '.pdf',
        maxFileSize: 2000000,
        mode: 'advanced',
        clientIdResolver: () => this.resolveUploadClientId(),
        uploaded: false,
        uploadedFilesNames: [],
        url: environment.serverUrl + 'ws/index.php?p1=upload&p2=upload_file&folder=ecorregimenes',
    };

    constructor(
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public fincasApi: FincasApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.agroambientales.PUT,
            tareasApi.agroambientales.POST,
            'tareas-agroambientales',
            `Editar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            `Añadir datos ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            `Duplicar tareas ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.softInit(this.getType());
            const routeModel = this.createRouteSnapshotModel();

            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel(this.toAgroambientalModel(routeModel));

            this.initialPreviousData = this.resolveInitialPreviousData(initialModel);
            await this.setupForm(initialModel);
            this.form.model = this.toAgroambientalModel(this.form.model);
            this.syncModelFromForm();
            this.prepareUploadState();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario agroambiental.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<AgroambientalModel>): void {
        const stableValue = this.captureStableValueChange(event.value as AgroambientalModel);

        this.form.model = this.toAgroambientalModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.prepareUploadState();
        this.cdr.markForCheck();
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<AgroambientalModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as AgroambientalModel);

        this.form.model = this.toAgroambientalModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.prepareUploadState();
        this.cdr.markForCheck();
    }

    public handleEventUpload(event: { originalEvent: unknown; files: File[] }): void {
        if (event.files.length === 0 || !this.form) {
            return;
        }

        const adjuntoGuia = event.files.map((file) => file.name).join(', ');

        this.form.model = this.toAgroambientalModel({
            ...this.form.model,
            adjunto_guia: adjuntoGuia,
        });
        this.syncModelFromForm();
        this.optionsUploadFile.uploaded = true;
        this.optionsUploadFile.uploadedFilesNames = event.files.map((file) => file.name);
        this.showDialogUploadFile = false;
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
        this.cdr.markForCheck();
    }

    public handleUploadedFileNamesChange(fileNames: string[]): void {
        if (!this.form) {
            return;
        }

        const uploadedFileNames = fileNames
            .map((fileName) => fileName.trim())
            .filter((fileName) => fileName.length > 0);
        const adjuntoGuia = uploadedFileNames.join(', ');

        this.form.model = this.toAgroambientalModel({
            ...this.form.model,
            adjunto_guia: adjuntoGuia,
        });
        this.syncModelFromForm();
        this.optionsUploadFile.uploaded = uploadedFileNames.length > 0;
        this.optionsUploadFile.uploadedFilesNames = uploadedFileNames;
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
        this.cdr.markForCheck();
    }

    public isPastoreoSelected(): boolean {
        return this.form?.isPastoreoSelected() ?? false;
    }

    private createRouteSnapshotModel(): Partial<AgroambientalModel> {
        const snapshotParams = this.route.snapshot.params as Record<string, unknown>;
        const routeModel: Record<string, AgroambientalModel[keyof AgroambientalModel]> = {};

        for (const [key, value] of Object.entries(snapshotParams)) {
            if (value !== undefined) {
                routeModel[key] = value as AgroambientalModel[keyof AgroambientalModel];
            }
        }

        return routeModel as Partial<AgroambientalModel>;
    }

    private resolveInitialPreviousData(initialModel: AgroambientalModel): AgroambientalModel | null {
        if (this.getType() === FormType.DUPLICATION) {
            return this.toAgroambientalModel(initialModel);
        }

        return null;
    }

    private async setupForm(initialModel: AgroambientalModel): Promise<void> {
        this.form = new AgroambientalesFormDefinition(this.getType(), initialModel);
        this.form.model = this.toAgroambientalModel(this.form.model);
        this.configureDeclarativeCatalogDataSources();

        await this.initFormData();
    }

    private initFormData(): Promise<void> {
        this.loadingData = true;
        this.loadingData = false;

        return Promise.resolve();
    }

    private configureDeclarativeCatalogDataSources(): void {
        this.form.setCatalogDataSources({
            fincas: this.fincasApi.fincasGET,
            sectores: this.fincasApi.sectoresGET,
            parcelas: this.fincasApi.parcelasGET,
            actividadesSobreCubierta: this.siexApi.actividadesSobreCubiertaGET,
            especiesAnimales: this.siexApi.especiesAnimalesGET,
            maquinarias: this.trabajadoresApi.maquinariaGET,
        });
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.cdr.markForCheck();
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario agroambiental: ' + error.message;
        }

        return 'No se ha podido cargar el formulario agroambiental. Revisa la conexión e inténtalo de nuevo.';
    }

    private resolveUploadClientId(): string {
        const modelClientId = this.resolvePrimitiveId(this.model.id_cliente);

        if (modelClientId.length > 0) {
            return modelClientId;
        }

        return this.resolveClientIdFromFinca(this.model.id_finca);
    }

    private resolveClientIdFromFinca(fincaValue: AgroambientalModel['id_finca']): string {
        if (this.isRecord(fincaValue)) {
            const directClientId = this.resolvePrimitiveId(fincaValue['id_cliente']);

            if (directClientId.length > 0) {
                return directClientId;
            }
        }

        const fincaId = this.resolvePrimitiveId(this.isRecord(fincaValue) ? fincaValue['id'] : fincaValue);

        if (fincaId.length === 0 || !this.form) {
            return '';
        }

        const fincaOptions = [
            ...this.form.fincas.values,
            ...this.form.fincas.filtered,
        ];

        for (const fincaOption of fincaOptions) {
            const sourceFinca = fincaOption.raw ?? fincaOption.value;

            if (!this.isRecord(sourceFinca)) {
                continue;
            }

            const sourceFincaId = this.resolvePrimitiveId(sourceFinca['id']);

            if (sourceFincaId !== fincaId) {
                continue;
            }

            const clientId = this.resolvePrimitiveId(sourceFinca['id_cliente']);

            if (clientId.length > 0) {
                return clientId;
            }
        }

        return '';
    }

    private resolvePrimitiveId(value: unknown): string {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value).trim();
        }

        if (this.isRecord(value)) {
            return this.resolvePrimitiveId(value['id']);
        }

        return '';
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private prepareUploadState(): void {
        const uploadedFileNames = this.model.adjunto_guia
            ? String(this.model.adjunto_guia).split(',').map((it) => it.trim()).filter((it) => it.length > 0)
            : [];

        this.optionsUploadFile.uploaded = uploadedFileNames.length > 0;
        this.optionsUploadFile.uploadedFilesNames = uploadedFileNames;
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toAgroambientalModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toAgroambientalModel(
        sourceModel: Partial<AgroambientalModel> | Record<string, unknown>,
    ): AgroambientalModel {
        const normalizedModel: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value;
            }
        }

        return normalizedModel as AgroambientalModel;
    }
}
